import { FC } from 'react';
import { CheckIcon } from './Icon';

import { OptionItem } from '../../types/components/common/select';

interface CheckboxGroupProps {
  label?: string;
  options: OptionItem[];
  values: string[];
  // eslint-disable-next-line no-unused-vars
  onChange: (newValues: string[]) => void;
  errorMessage?: string;
}

/**
 * RadioGroup と同じ見た目・構造の複数選択版。
 * Headless UI の RadioGroup は単一選択専用のため、ここでは
 * <label> + <input type="checkbox"> の組み合わせでアクセシブルな実装にする
 * （role="checkbox" / aria-checked が testing-library から取得できる）。
 */
const CheckboxGroup: FC<CheckboxGroupProps> = ({ label, options, values, onChange, errorMessage }) => {
  const handleToggle = (optionValue: string) => {
    const checked = values.includes(optionValue);
    if (checked) {
      onChange(values.filter((v) => v !== optionValue));
    } else {
      onChange([...values, optionValue]);
    }
  };

  return (
    <div role='group' aria-label={label}>
      {label && <p>{label}</p>}
      {options.map((option) => {
        const checked = values.includes(option.value);
        return (
          <label
            key={option.value}
            className='py-4 relative flex cursor-pointer focus:outline-none bg-white items-center gap-4'
          >
            <input
              type='checkbox'
              aria-checked={checked}
              aria-label={option.text}
              checked={checked}
              onChange={() => handleToggle(option.value)}
              className='sr-only'
            />
            {checked ? (
              <CheckIcon checked className='fill-primary stroke-primary' />
            ) : (
              <CheckIcon className='stroke-gray-medium/70' />
            )}
            <p className={`text-lg font-normal ${checked ? 'text-primary' : 'text-black'}`}>{option.text}</p>
          </label>
        );
      })}
      {errorMessage && <p className='mt-2 text-sm text-red'>{errorMessage}</p>}
    </div>
  );
};

export default CheckboxGroup;
